// New ArticleDetail.vue with improved group-content handling

<template>
  <div class="article-detail-page">
    <SideBar
      :groups="groups"
      :isSidebarOpen="isSidebarOpen"
      @toggle-sidebar="toggleSidebar"
      @close-sidebar="closeSidebar"
      @handle-logout="handleLogout"
    />
    <div class="main-content">
      <TopBar
        :source="tag"
        @toggle-sidebar="toggleSidebar"
        @selected="getSelected"
      />
      <div class="content-area">
        <Breadcrumb
          :items="[
            { text: 'Dashboard', link: '/dashboard' },
            ...($route.params.groupId
              ? [
                  {
                    text: currentGroupName,
                    link: `/article/${$route.params.groupId}`,
                  },
                ]
              : []),
            { text: getBreadcrumbTitle(), link: null },
          ]"
        />

        <div v-if="isLoading" class="loading-container">
          <LoadingSpinner size="medium" color="blue" />
        </div>

        <div v-else-if="!articleDetail" class="error-container">
          <i class="material-icons">error</i>
          <p>Article not found.</p>
          <router-link to="/articles" class="btn-flat blue-text">
            Back to Knowledge Base
          </router-link>
        </div>
        <div v-else class="article-detail-container">
          <SectionContainer>
            <div class="article-header">
              <h1 class="article-title">
                {{
                  articleDetail.ArticleTitle ||
                  articleDetail.LinkName ||
                  articleDetail.GroupName ||
                  "Article Detail"
                }}
              </h1>
              <div class="article-meta">
                <span v-if="articleDetail.Created" class="article-date">{{
                  formatDate(articleDetail.Created)
                }}</span>
              </div>
            </div>
            <!-- Display embedded iframe for Google Drive links -->
            <div v-if="isEmbedMode && embedUrl" class="embed-container">
              <iframe
                :src="embedUrl"
                frameborder="0"
                width="100%"
                height="600"
                allowfullscreen
              ></iframe>

              <!-- Display LinkText content below the embedded document if available -->
              <div
                v-if="articleDetail.LinkText || articleDetail.ArticleContent"
                class="article-content mt-4"
                v-html="articleDetail.ArticleContent || articleDetail.LinkText"
                ref="articleContent"
              ></div>
            </div>
            <!-- Display external link content -->
            <div
              v-else-if="externalUrl && !isEmbedMode"
              class="external-link-container"
            >
              <!-- Display LinkText content if available -->
              <div
                v-if="articleDetail.LinkText || articleDetail.ArticleContent"
                class="article-content"
                v-html="articleDetail.ArticleContent || articleDetail.LinkText"
                ref="articleContentExt"
              ></div>
              <!-- If no content available, show a message -->
              <div v-else class="no-content-message">
                <p>
                  <i class="material-icons">info</i>
                  No additional content available for this resource.
                </p>
              </div>
            </div>
            <!-- Display regular article content -->
            <div v-else>
              <div
                v-if="
                  articleDetail.ArticleShort ||
                  articleDetail.LinkDescription ||
                  articleDetail.GroupDescription
                "
                class="article-short"
              >
                <p>
                  {{
                    articleDetail.ArticleShort ||
                    articleDetail.LinkDescription ||
                    articleDetail.GroupDescription
                  }}
                </p>
              </div>
              <div
                class="article-content"
                v-html="articleDetail.ArticleContent || articleDetail.LinkText"
                ref="articleContent"
              ></div>
            </div>
            <div class="article-actions">
              <router-link
                :to="
                  $route.params.groupId
                    ? `/article/${$route.params.groupId}`
                    : '/articles'
                "
                class="btn-flat blue-text"
              >
                <i class="material-icons">arrow_back</i>
                Back to Articles
              </router-link>
            </div>
          </SectionContainer>
        </div>
      </div>

      <AppFooter />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import router from "../router";
import config from "../config.js";
import SideBar from "./SideBar.vue";
import TopBar from "./TopBar.vue";
import AppFooter from "./AppFooter.vue";
import SectionContainer from "./SectionContainer.vue";
import Breadcrumb from "./Breadcrumb.vue";
import LoadingSpinner from "./LoadingSpinner.vue";
import "../assets/css/dashboard.css";

export default {
  name: "ArticleDetail",
  components: {
    SideBar,
    TopBar,
    AppFooter,
    SectionContainer,
    Breadcrumb,
    LoadingSpinner,
  },
  computed: {
    currentGroupName() {
      if (!this.$route.params.groupId || !this.groups.length) return "";
      const groupId = this.$route.params.groupId;
      const group = this.groups.find(
        (g) => g.GroupID === groupId || g.GroupID === parseInt(groupId)
      );
      return group ? group.GroupName : "Articles";
    },
  },
  data() {
    return {
      articleDetail: null,
      groups: [],
      user: [],
      tag: [],
      isLoading: true,
      isSidebarOpen: false,
      usercookie: null,
      embedUrl: null,
      externalUrl: null,
      isEmbedMode: false,
    };
  },
  methods: {
    getToken() {
      return this.$cookies.get("arch-it-portal") || this.usercookie;
    },
    formatDate(dateString) {
      if (!dateString) return "";

      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid

      const day = date.getDate();
      const month = date.toLocaleString("en-US", { month: "long" });
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    },
    getArticleDetail() {
      this.isLoading = true;
      const linkId = this.$route.params.linkId;
      const token = this.getToken();

      console.log("Getting article detail for linkId:", linkId);
      const apiUrl = config.getApiUrl(`articles/${linkId}`);
      console.log("Article detail API URL:", apiUrl);

      axios
        .get(
          apiUrl,
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => {
          console.log("Article detail response:", response.data);

          if (response.data) {
            this.articleDetail = response.data;

            // Check if there's a HyperLink in the article data
            if (response.data.HyperLink) {
              console.log(
                "HyperLink found in article detail:",
                response.data.HyperLink
              );
              this.externalUrl = response.data.HyperLink;

              // If it's a Google Drive link, set it up for embedding
              if (this.externalUrl.includes("drive.google.com")) {
                this.isEmbedMode = true;
                this.embedUrl = this.convertGoogleDriveUrl(this.externalUrl);
                console.log("Converted embed URL:", this.embedUrl);
              }
            }
          } else {
            console.log("Empty article detail response");
          }

          this.isLoading = false;
        })
        .catch((error) => {
          console.error("Error fetching article details:", error);
          console.log(
            "Error details:",
            error.response ? error.response.data : "No response data"
          );

          // Set a basic error state
          this.articleDetail = null;
          this.isLoading = false;
        });
    },
    getGroups() {
      const token = this.getToken();
      axios
        .get(
          config.getApiUrl("group"),
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => (this.groups = response.data));
    },
    getTag() {
      const token = this.getToken();
      axios
        .get(
          config.getApiUrl("get-tag"),
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => (this.tag = response.data));
    },
    getUser() {
      let params = new URLSearchParams();
      params.append("user", this.usercookie);
      const token = this.getToken();

      axios({
        method: "post",
        url: config.getApiUrl("get-user"),
        data: params,
        ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      }).then((response) => {
        this.loginStatus(response.data);
      });
    },
    loginStatus(userData) {
      if (
        !userData ||
        !userData.expire ||
        Number(userData.expire) < Math.floor(Date.now() / 1000)
      ) {
        router.push({
          name: "Login",
        });
      }
    },
    handleLogout() {
      this.$cookies.remove("arch-it-portal");
      router.push({
        name: "Login",
      });
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
      document.body.style.overflow = this.isSidebarOpen ? "hidden" : "";
    },
    closeSidebar() {
      this.isSidebarOpen = false;
      document.body.style.overflow = "";
    },
    getSelected(result) {
      window.open(result.value, "_blank");
    },
    handleResize() {
      if (window.innerWidth > 768 && this.isSidebarOpen) {
        this.closeSidebar();
      }
    },
    getBreadcrumbTitle() {
      if (!this.articleDetail) return "Loading...";

      // If it's a regular article, use its title
      if (this.articleDetail.ArticleTitle) {
        return this.articleDetail.ArticleTitle;
      }

      // For embedded Google Drive documents
      if (this.isEmbedMode) {
        return this.articleDetail.LinkName || "Embedded Document";
      }

      // For external resources
      if (this.externalUrl) {
        return this.articleDetail.LinkName || "External Resource";
      }

      // Fallback to any available name
      return (
        this.articleDetail.LinkName || this.articleDetail.GroupName || "Article"
      );
    },
    applyStylesToContent() {
      this.$nextTick(() => {
        // Apply styles to main content area
        if (this.$refs.articleContent) {
          const olElements = this.$refs.articleContent.querySelectorAll("ol");
          olElements.forEach((ol) => {
            ol.style.paddingLeft = "25px";
          });
        }

        // Apply styles to content in external link section
        if (this.$refs.articleContentExt) {
          const olElements =
            this.$refs.articleContentExt.querySelectorAll("ol");
          olElements.forEach((ol) => {
            ol.style.paddingLeft = "25px";
          });
        }
      });
    },
    convertGoogleDriveUrl(url) {
      // Convert Google Drive links to embedded format
      if (!url) return null;

      // Extract file ID from various Google Drive URL formats
      let fileId = "";

      if (url.includes("/file/d/")) {
        // Format: https://drive.google.com/file/d/FILE_ID/view
        const matches = url.match(/\/file\/d\/([^\/]+)/);
        if (matches && matches[1]) {
          fileId = matches[1];
        }
      } else if (url.includes("id=")) {
        // Format: https://drive.google.com/open?id=FILE_ID
        const matches = url.match(/id=([^&]+)/);
        if (matches && matches[1]) {
          fileId = matches[1];
        }
      }

      if (fileId) {
        // Check if the URL points to a Google Doc, Sheet, etc.
        if (
          url.includes("document") ||
          url.includes("spreadsheets") ||
          url.includes("presentation")
        ) {
          return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(
            url
          )}`;
        }

        // For regular files
        return `https://drive.google.com/file/d/${fileId}/preview`;
      }

      // If we can't parse it, just use the preview format if it's a /view URL
      if (url.includes("/view")) {
        return url.replace("/view", "/preview");
      }

      return url;
    },
    fetchExternalUrl() {
      this.isLoading = true;
      const linkId = this.$route.params.linkId;
      const groupId = this.$route.params.groupId || "1"; // Use the provided groupId or default to "1"
      const token = this.getToken();

      console.log(
        "Fetching from group-content API for linkId:",
        linkId,
        "groupId:",
        groupId
      );

      // Use the group-content endpoint with the provided groupId
      const apiUrl = config.getApiUrl(`group-content/${groupId}/30/show`);
      console.log("API URL:", apiUrl);

      axios
        .get(
          apiUrl,
          token
            ? {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            : {}
        )
        .then((response) => {
          console.log("Group content response:", response.data);

          // Find the matching item by LinkID or GroupID
          if (response.data && Array.isArray(response.data)) {
            const item = response.data.find((item) => {
              return (
                linkId &&
                (item.LinkID === linkId ||
                  item.LinkID === parseInt(linkId) ||
                  item.GroupID === linkId ||
                  item.GroupID === parseInt(linkId))
              );
            });

            if (item) {
              console.log("Found matching item:", item);
              // Handle item based on its content
              if (item.HyperLink) {
                console.log("HyperLink found:", item.HyperLink);
                this.externalUrl = item.HyperLink;

                // Set article details
                this.articleDetail = {
                  ArticleTitle:
                    item.LinkName || item.GroupName || "External Resource",
                  ArticleShort:
                    item.LinkDescription || item.GroupDescription || "",
                  LinkText: item.LinkText || "",
                  Created: item.Created || "",
                };

                // If item also has LinkText, include it in the article details
                if (item.LinkText) {
                  console.log("Item has both HyperLink and LinkText");
                  this.articleDetail.ArticleContent = item.LinkText;
                }

                // Handle Google Drive links
                if (this.externalUrl.includes("drive.google.com")) {
                  this.isEmbedMode = true;
                  this.embedUrl = this.convertGoogleDriveUrl(this.externalUrl);
                  console.log("Embed URL set:", this.embedUrl);
                }
              }
              // If no HyperLink but has LinkText, use it as content
              else if (item.LinkText) {
                console.log("Using LinkText as content");
                this.articleDetail = {
                  ArticleTitle: item.LinkName || item.GroupName || "Article",
                  ArticleShort:
                    item.LinkDescription || item.GroupDescription || "",
                  ArticleContent: item.LinkText,
                  Created: item.Created || "",
                };
              }
              // Otherwise just use the basic item info
              else {
                console.log("Using basic item info");
                this.articleDetail = {
                  ArticleTitle: item.LinkName || item.GroupName || "Article",
                  ArticleShort:
                    item.LinkDescription || item.GroupDescription || "",
                  Created: item.Created || "",
                };
              }

              this.isLoading = false;
            } else {
              console.log("No matching item found in the array");
              this.articleDetail = null;
              this.isLoading = false;
            }
          } else {
            console.log("Response is not an array or is empty");
            this.articleDetail = null;
            this.isLoading = false;
          }
        })
        .catch((error) => {
          console.error("Error fetching group content:", error);
          console.log(
            "Error details:",
            error.response ? error.response.data : "No response data"
          );
          this.articleDetail = null;
          this.isLoading = false;
        });
    },
  },
  created() {
    this.usercookie = this.$cookies.get("arch-it-portal");
    this.getUser();
    this.getGroups();
    this.getTag();

    console.log("===== ARTICLE DETAIL COMPONENT CREATED =====");
    console.log("Route name:", this.$route.name);
    console.log("Route params:", this.$route.params);
    console.log("Route query:", this.$route.query);

    // Determine what type of article detail we're showing based on the route
    const routeName = this.$route.name;
    const urlFromQuery = this.$route.query.url;
    const groupId = this.$route.params.groupId || "1";

    // For Google Drive embedded content
    if (
      routeName === "ArticleDetailEmbed" ||
      (routeName === "ArticleDetail" && this.$route.query.embed === "true")
    ) {
      console.log("Handling embedded content");
      this.isEmbedMode = true;

      // If there's a URL in query parameters, use it directly
      if (urlFromQuery) {
        console.log("URL from query params:", urlFromQuery);
        this.externalUrl = urlFromQuery;
        this.embedUrl = this.convertGoogleDriveUrl(this.externalUrl);
        console.log("Converted embed URL:", this.embedUrl);

        // Set basic article detail for embedded content
        this.articleDetail = {
          ArticleTitle: "Embedded Document",
          ArticleShort: "This document is embedded from an external source.",
        };

        this.isLoading = false;
      }
      // Otherwise fetch from the group-content API
      else {
        console.log("Fetching from group-content API with groupId:", groupId);
        this.fetchExternalUrl();
      }
    }
    // For external links
    else if (
      routeName === "ArticleDetailExternal" ||
      (routeName === "ArticleDetail" &&
        urlFromQuery &&
        !this.$route.query.embed)
    ) {
      console.log("Handling external URL");

      // If there's a URL in query parameters, use it directly
      if (urlFromQuery) {
        console.log("External URL from query params:", urlFromQuery);
        this.externalUrl = urlFromQuery;

        // Set basic article detail for external link
        this.articleDetail = {
          ArticleTitle: "External Resource",
          ArticleShort: "This is a link to an external resource.",
        };

        this.isLoading = false;
      }
      // Otherwise fetch from the group-content API
      else {
        console.log("Fetching from group-content API with groupId:", groupId);
        this.fetchExternalUrl();
      }
    }
    // For regular article detail
    else {
      console.log(
        "Handling regular article from group-content API with groupId:",
        groupId
      );
      this.fetchExternalUrl();
    }
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
    this.applyStylesToContent();
  },
  updated() {
    this.applyStylesToContent();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.article-detail-page {
  display: flex;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 50px 0;
  text-align: center;
}

.error-container i {
  font-size: 48px;
  color: #e0e0e0;
  margin-bottom: 15px;
}

.error-container p {
  font-size: 16px;
  color: #757575;
  margin-bottom: 20px;
}

.article-detail-container {
  max-width: 1000px;
  margin: 0 auto;
}

.article-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
}

.article-title {
  font-size: 2rem;
  color: #00205b;
  margin: 0 0 8px 0;
  line-height: 1.3;
}

.mt-4 {
  margin-top: 1.5rem;
}

.article-meta {
  display: flex;
  align-items: center;
  color: #757575;
  font-size: 0.9rem;
}

.article-date {
  padding-right: 15px;
}

.article-short {
  font-size: 1.1rem;
  color: #555;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
  font-weight: 500;
}

.article-content {
  line-height: 1.6;
  color: #333;
}

.article-content :deep(p) {
  margin-bottom: 16px;
}

.article-content :deep(h2) {
  margin-top: 32px;
  margin-bottom: 16px;
  color: #00205b;
  font-size: 1.5rem;
}

.article-content :deep(a) {
  color: #1565c0;
  text-decoration: none;
}

.article-content :deep(a:hover) {
  text-decoration: underline;
}

.article-content :deep(ul) {
  margin-bottom: 16px;
  padding-left: 20px;
}

.article-content :deep(ol) {
  margin-bottom: 16px;
  padding-left: 25px !important;
}

.article-content :deep(li) {
  margin-bottom: 8px;
}

/* Additional selector to ensure ordered lists have 25px padding */
.article-detail-container .article-content ol {
  padding-left: 25px !important;
}

.article-content :deep(hr) {
  margin: 24px 0;
  border: 0;
  height: 1px;
  background-color: #eaeaea;
}

.article-actions {
  margin-top: 32px;
  padding-top: 16px;
  border-top: 1px solid #eaeaea;
}

.embed-container {
  margin: 20px 0;
  border: 1px solid #eaeaea;
  border-radius: 4px;
  overflow: hidden;
}

.external-link-container {
  margin: 20px 0;
  padding: 20px;
  background-color: #ffffff;
  border-radius: 4px;
  text-align: left;
}

.external-link-container .article-content {
  margin-top: 0;
  padding-top: 0;
  text-align: left;
}

.no-content-message {
  text-align: center;
  padding: 30px 0;
  color: #757575;
}

.no-content-message i {
  font-size: 36px;
  display: block;
  margin-bottom: 10px;
  color: #e0e0e0;
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  background-color: #00205b;
  color: white;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-primary:hover {
  background-color: #003080;
  transform: translateY(-2px);
}

.btn-primary i {
  font-size: 18px;
  margin-right: 8px;
}

.btn-flat {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.3s ease;
  background-color: rgba(33, 150, 243, 0.05);
  font-weight: 500;
}

.btn-flat:hover {
  background-color: rgba(33, 150, 243, 0.15);
  transform: translateY(-2px);
}

.btn-flat i {
  font-size: 18px;
  margin-right: 6px;
}

@media (max-width: 768px) {
  .article-title {
    font-size: 1.5rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ArticleDetail.vue